import { jsxs, jsx } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Stack, Inline, Text, Box, Tag } from "braid-design-system";
import { useRef, useEffect, useCallback, memo } from "react";
import { createValidator } from "@seek/validators-js";
import { translations } from "../../../../components/SkillsForm/.vocab/index.mjs";
import { translations as translations$1 } from "../../../../components/SkillsForm/Skills/.vocab/index.mjs";
import { tagAnimation } from "./Skills.css.mjs";
const SKILL_TAGS_STACK_SPACE = "small";
const SKILL_TAGS_INLINE_SPACE = "small";
const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
const getSkillsStructuredData = ({ value }) => ({
  ontologyId: value
});
const getValidationValue = (skill) => skill.keyword.text;
const getSuggestedSkills = ({
  initialSuggestedSkills,
  initialSkills
}) => initialSuggestedSkills.filter((initialSuggestedSkill) => {
  const initialSkillsContainsSuggestedSkill = initialSkills.some(
    (initialSkill) => initialSkill.keyword.text === initialSuggestedSkill.keyword.text
  );
  return !initialSkillsContainsSuggestedSkill;
});
const useShouldAnimateSkill = (skills) => {
  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const previousSkills = usePrevious(skills) || [];
  const newlyAddedSkills = skills.filter((x) => !previousSkills.includes(x));
  return useCallback(
    (skill) => !initialRender.current && newlyAddedSkills.includes(skill),
    [newlyAddedSkills]
  );
};
const MAX_SKILLS = 50;
const { maxSkills } = createValidator({
  test: ({
    validatorProps: { hasMaxSkills },
    value
  }) => {
    if (hasMaxSkills && value) {
      return false;
    }
    return true;
  },
  formatErrorMessages: (messages) => () => messages["You can't have more than maximumSkills skills"].format({
    maximumSkills: MAX_SKILLS.toString()
  }),
  translations,
  validatorName: "maxSkills"
});
const SkillsComponent = ({ skills, clearSkill }) => {
  const { t } = useTranslations(translations$1);
  const hasTooManySkills = skills.length > MAX_SKILLS;
  const shouldAnimateSkill = useShouldAnimateSkill(skills);
  return /* @__PURE__ */ jsxs(Stack, { space: SKILL_TAGS_STACK_SPACE, children: [
    /* @__PURE__ */ jsxs(Inline, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Text, { weight: "strong", children: t("Added skills") }),
      skills.length >= 40 ? /* @__PURE__ */ jsxs(Text, { tone: hasTooManySkills ? "critical" : "secondary", children: [
        "(",
        skills.length,
        "/",
        MAX_SKILLS,
        ")"
      ] }) : null
    ] }),
    skills.length === 0 ? /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("No skills have been added") }) : /* @__PURE__ */ jsx(Box, { "data-testid": "added-skills", children: /* @__PURE__ */ jsx(Inline, { space: SKILL_TAGS_INLINE_SPACE, component: "ul", children: skills.map((skill, i) => {
      const showScaleAnimation = shouldAnimateSkill(skill);
      const tagStyles = { [tagAnimation]: showScaleAnimation };
      return /* @__PURE__ */ jsx(
        Box,
        {
          className: tagStyles,
          children: /* @__PURE__ */ jsx(
            Tag,
            {
              id: `added-skill-${i}`,
              onClear: () => clearSkill(i),
              clearLabel: t("Remove skill text", {
                skill: skill.keyword.text
              }),
              children: skill.keyword.text
            }
          )
        },
        `${skill.keyword.text}-${skill.keyword.ontologyId}-${i}`
      );
    }) }) })
  ] });
};
const Skills = memo(
  SkillsComponent,
  ({ skills: prevSkills }, { skills: nextSkills }) => JSON.stringify(prevSkills) === JSON.stringify(nextSkills)
);
export {
  MAX_SKILLS,
  SKILL_TAGS_INLINE_SPACE,
  SKILL_TAGS_STACK_SPACE,
  Skills,
  getSkillsStructuredData,
  getSuggestedSkills,
  getValidationValue,
  maxSkills,
  useShouldAnimateSkill
};
